/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useRouter } from 'next/router'
import Button from '@material-ui/core/Button'
import {
  pushDangerNotification,
  pushSuccessNotification,
  TranslationDictionary,
  useTranslation
} from '@plandi/common'

import { URLS } from 'src/common'
import { useThunkDispatch } from 'src/store'
import {
  FormValuesProjectRequest,
  getImagesForFormattedForm,
  getPartsForFormattedForm,
  getSaveProjectValidationSchema,
  ProjectForm,
  useMutationUpdateProject,
  useQueryProjectMeById
} from '../common'
import { LoadinAction } from 'src/reducers/actions/LoadingActions'

type ProjectUpdateProps = {
  translate?: TranslationDictionary['data']
  locale: string
}

export const ProjectUpdate: React.FC<ProjectUpdateProps> = () => {
  const router = useRouter()
  const dispatch = useThunkDispatch()
  const { data: translate } = useTranslation()
  const projectId = router.query.projectId
    ? Number(router.query.projectId)
    : undefined

  const { data: project } = useQueryProjectMeById(projectId)

  const formik = useFormik<FormValuesProjectRequest>({
    initialValues: {
      name: project?.name ?? translate?.shop['unnamed-project'] ?? '',
      cost: project?.cost,
      negotiated: project?.isNegotiated ?? false,
      designYear: project?.designYear,
      categoryId: project?.category?.id ?? null,
      subCategoryId: project?.subCategory?.id ?? null,
      projectStaging: project?.projectStaging,
      expertise: project?.expertise ?? false,
      bimModel: project?.bimModel ?? false,
      fileFormats: project?.fileFormats ?? [],
      landArea: project?.landArea,
      builtUpArea: project?.builtUpArea,
      buildingArea: project?.buildingArea,
      buildingHeight: project?.buildingHeight,
      numberOfFloors: project?.numberOfFloors,
      appointment: project?.appointment ?? '',
      projectComposition: project?.projectComposition ?? '',
      notes: project?.notes ?? '',
      parts: getPartsForFormattedForm(project?.parts),
      status: project?.projectStatus ?? 'draft',
      ...getImagesForFormattedForm(project?.cover, project?.images)
    },
    enableReinitialize: true,
    validationSchema: getSaveProjectValidationSchema(translate),
    onSubmit: (values) => {
      dispatch(LoadinAction(true))
      mutationUpdateProject.mutate(values)
    }
  })

  const isPublish = project?.projectStatus === 'published'

  const mutationUpdateProject = useMutationUpdateProject(projectId, {
    onSuccess: () => {
      const message =
        (isPublish
          ? translate?.['project-saved-successfully']
          : translate?.['project-saved-successfully']) ?? ''
      dispatch(pushSuccessNotification(message))

      router.push(URLS.projects.me)
    },
    onError: (error) => {
      dispatch(
        pushDangerNotification(error.response?.data?.message || error.message)
      )
      dispatch(LoadinAction(false))
      formik.setFieldValue('status', project?.projectStatus)
    },
    onSettled: () => {
      dispatch(LoadinAction(false))
      formik.setSubmitting(false)
    }
  })

  const handleDraftProject = async () => {
    const values = {
      ...formik.values,
      status: 'draft' as const
    }

    const errors = await formik.validateForm(values)

    const isValid = Object.keys(errors).length === 0

    if (isValid) {
      formik.setFieldValue('status', 'draft')

      formik.handleSubmit()
    }
  }

  const handlePublishProject = async () => {
    const values = {
      ...formik.values,
      status: 'published' as const
    }

    const errors = await formik.validateForm(values)

    const isValid = Object.keys(errors).length === 0

    if (!isValid) {
      dispatch(
        pushDangerNotification(translate?.shop['required-not-filled'] ?? '')
      )

      return
    }

    formik.setFieldValue('status', 'published')

    formik.handleSubmit()
  }

  return (
    <FormikProvider value={formik}>
      <ProjectForm
        publishedAt={project?.publishedAt}
        profileType={project?.type}
        buttons={
          <>
            {project?.isBlocked ? (
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={formik.isSubmitting || !formik.dirty}
              >
                {translate?.shop['save-submit-review']}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={formik.isSubmitting || !formik.dirty}
                >
                  {translate?.shop['add-model-page']['save']}
                </Button>
                {isPublish ? (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.dirty}
                    onClick={handleDraftProject}
                  >
                    {translate?.shop['save-and-unpublish']}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.dirty}
                    onClick={handlePublishProject}
                  >
                    {translate?.shop['save-and-publish']}
                  </Button>
                )}
              </>
            )}
          </>
        }
      />
    </FormikProvider>
  )
}
